import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { CustomTemplatesDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  DuplicateCustomTemplateDocument,
  ReviewProcessCustomTemplateDocument,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { TemplatePreview } from 'modules/performance_process/components/templates/TemplatePreview/TemplatePreview'
import { CustomTemplate } from 'modules/performance_process/components/templates/types'
import { DeleteCustomTemplateModal } from './DeleteCustomTemplateModal'

type Props = {
  template: CustomTemplate
  canManage: boolean
  canDuplicate: boolean
}

export const Actions: React.FC<Props> = ({ template, canManage, canDuplicate }) => {
  const { access } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [showDeleteTemplateModal, toggleShowDeleteTemplateModal] = useToggle()
  const [previewSidepanelOpen, togglePreviewSidepanelOpen] = useToggle()

  const [duplicateTemplate] = useMutation(DuplicateCustomTemplateDocument, {
    extractorFn: (data) =>
      data.performanceMutations.duplicatePerformanceReviewProcessCustomTemplate,
  })

  const handleDuplicateTemplate = async () => {
    const response = await duplicateTemplate({
      awaitRefetchQueries: true,
      variables: {
        id: template.id,
        authorId: access?.id,
      },
      refetchQueries: [CustomTemplatesDataTableDocument],
    })

    const handled = handleResult(response, () => undefined)

    if (handled.success) {
      const templateId = handled?.data?.reviewProcessCustomTemplate?.id

      app.setFlash({
        type: 'correct',
        message: i18n.t('performance_process.reviews.duplicate_review.modal.success_message'),
      })

      if (templateId) {
        navigate(
          navigation.performanceProcess.reviews.templates.template.fullPath({
            templateId: templateId,
          })
        )
      }
    }
  }

  return (
    <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
      {(canManage || canDuplicate) && (
        <Box flexDirection="row" justifyContent="flexEnd" alignItems="center" gap="s8">
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button.Decorative type="secondary" iconName="ellipsis" />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="start">
              {canManage && (
                <PopoverMenu.RouterOption
                  to={navigation.performanceProcess.reviews.templates.template.fullPath({
                    templateId: template.id,
                  })}
                  title={i18n.t('performance_process.reviews.go_to_edit_review_button')}
                />
              )}
              {canDuplicate && (
                <PopoverMenu.TouchableOption
                  title={i18n.t(
                    'performance_process.reviews_templates.duplicate_template.duplicate'
                  )}
                  onClick={handleDuplicateTemplate}
                />
              )}
              {canManage && (
                <PopoverMenu.TouchableOption
                  danger
                  title={i18n.t('performance_process.reviews.delete_review.delete')}
                  onClick={toggleShowDeleteTemplateModal(true)}
                />
              )}
            </PopoverMenu.Content>
          </PopoverMenu>
        </Box>
      )}
      <Button
        type="secondary"
        onClick={togglePreviewSidepanelOpen(true)}
        iconName="eye-visible"
        aria-label={i18n.t('performance_process.reviews.templates.template_preview')}
      />

      {showDeleteTemplateModal && (
        <DeleteCustomTemplateModal
          templateId={template.id}
          onClose={toggleShowDeleteTemplateModal(false)}
        />
      )}

      {previewSidepanelOpen && (
        <Sidepanel
          open
          onClose={togglePreviewSidepanelOpen(false)}
          width="wideXL"
          title={i18n.t('performance_process.reviews.templates.template_preview')}
        >
          <TemplatePreview template={template} query={ReviewProcessCustomTemplateDocument} />
        </Sidepanel>
      )}
    </Box>
  )
}
